import Logger from '../util/Logger';
import { dashApi, popinApi } from './api';
import Event from '../models/Event';

const joinEvent = (eventId) => {
  return Promise.resolve().then(() => {
    if (!eventId) {
      Logger.info('reject join()');
      throw Error('Must pass an eventId as a param');
    } else {
      return popinApi.post(`/events/join/${eventId}`);
    }
  });
};

const fetchPosts = (eventId, includeChildren) => {
  return Promise.resolve().then(() => {
    if (!eventId) {
      Logger.info('reject fetchPosts()');
      throw Error('Must pass an eventId as a param');
    } else {
      return popinApi.get(`/events/${eventId}/posts?include_children=${includeChildren}`);
    }
  });
};

const statsCall = (endpoint) => {
  return dashApi.get(endpoint);
};

export const eventApi = {

  get: (eventId) => {
    return popinApi.get(`/events/detail/${eventId}`);
  },

  create: (event) => {
    return popinApi.post(`/events/create`, event);
  },

  update: (event) => {
    return popinApi.post(`/events/edit`, event);
  },

  publish: (eventID) => {
    return popinApi.post(`/events/publish/${eventID}`);
  },

  join: (eventId) => {
    return joinEvent(eventId);
  },

  deleteEvent: (eventId) => {
    return popinApi.del(`/events/delete/${eventId}`);
  },

  deleteCollection: (collId) => {
    return popinApi.del(`/collections/${collId}`);
  },

  getDashboardEvents: () => {
    return popinApi.get(`/events?type=mini`);
  },

  getDetails: (eventId) => {
    // console.log('========== RESULTS TAB: EventApi.getDetails CALLED ==========');
    // console.log('Input eventId:', eventId);
    
    return joinEvent(eventId).then(event => {
      const cleanEventId = Event.getId(event);
      // console.log('Joined event, extracted clean eventId:', cleanEventId);
      const url = `/events/detail/${cleanEventId}`;
      // console.log('API URL:', url);
      
      return popinApi.get(url)
        .then(response => {
          // console.log('EventApi.getDetails response summary:');
          // console.log('- Event title:', response.title);
          //console.log('- Event fields available:', Object.keys(response));
          /* console.log('- Event details sample:', JSON.stringify({
            id: response.id,
            title: response.title,
            description: response.description,
            created_time: response.created_time,
            question_type: response.question_type,
            status: response.status
          }, null, 2)); */
          return response;
        })
        .catch(error => {
          console.error('EventApi.getDetails error:', error);
          console.error('Error status:', error.response?.status);
          console.error('Error data:', error.response?.data);
          throw error; // Re-throw to be handled by the caller
        });
    });
  },

  getStats: (eventId) => {
    // console.log('EventApi.getStats called with eventId:', eventId);
    const url = `/events/${eventId}/stats`;
    // console.log('API URL:', url);
    
    return statsCall(url)
      .then(response => {
        // console.log('EventApi.getStats response:', JSON.stringify(response, null, 2));
        //console.log('Stats fields available:', Object.keys(response));
        return response;
      })
      // For sagas
      .catch(error => {
        console.error('EventApi.getStats error:', error);
        console.error('Error status:', error.response?.status);
        console.error('Error data:', error.response?.data);
        // This will be handled in the saga
        return { statsError: error };
      });
  },

  getMiniStats: (eventId) => {
    return statsCall(`/events/${eventId}/mini/stats`);
  },

  getPulseStats: (eventId) => {
    return Promise.resolve().then(() => {
      if (!eventId) {
        //Logger.info('reject getPulseStates()');
        throw Error('Must pass an eventId as a param');
      } else {
        return dashApi.get(`/events/${eventId}/pulses?show_all=true`);
      }
    }).catch(error => {
      // This will be handled in the saga
      return { pulseError: error };
    });
  },

  getSentimentByRating: (eventId) => {
    return Promise.resolve().then(() => {
      if (!eventId) {
        Logger.info('reject getSentimentByRating()');
        throw Error('Must pass an eventId as a param');
      } else {
        return dashApi.get(`/events/${eventId}/distributions/votes`);
      }
    }).catch(error => {
      // This will be handled in the saga
      return { sentimentByRatingError: error };
    });
  },

  getRatingDistribution: (eventId, ideaId) => {
    return Promise.resolve().then(() => {
      if (!eventId) {
        Logger.info('reject getRatingDistribution()');
        throw Error('Must pass an eventId as a param');
      } else {
        return dashApi.get(`/events/${eventId}/ideas/${ideaId}/rating-distribution`);
      }
    }).catch(error => {
      // This will be handled in the saga
      return { sentimentByRatingError: error };
    });
  },

  fetchAllPosts: (eventId) => {
    // console.log('========== RESULTS TAB: EventApi.fetchAllPosts CALLED ==========');
    // console.log('Input eventId:', eventId);
    // console.log('API URL:', `/events/${eventId}/posts?include_children=true`);
    
    return fetchPosts(eventId, true)
      .then(response => {
        // console.log('EventApi.fetchAllPosts response summary:');
        // console.log('- Total posts:', response.posts?.length || 0);
        if (response.posts && response.posts.length > 0) {
          // console.log('- First post sample:', JSON.stringify(response.posts[0], null, 2));
          // console.log('- Post fields available:', Object.keys(response.posts[0]));
        }
        return response;
      })
      .catch(error => {
        console.error('EventApi.fetchAllPosts error:', error);
        console.error('Error status:', error.response?.status);
        console.error('Error data:', error.response?.data);
        // This will be handled in the saga
        return { postsError: error };
      });
  },

  fetchNoteworthy: (eventId) => {
    // console.log('========== RESULTS TAB: EventApi.fetchNoteworthy CALLED ==========');
    // console.log('Input eventId:', eventId);
    
    return Promise.resolve().then(() => {
      if (!eventId) {
        Logger.info('reject fetchNoteworthy()');
        const error = new Error('Must pass an eventId as a param');
        console.error('Invalid eventId for fetchNoteworthy:', error);
        throw error;
      } else {
        const url = `/events/${eventId}/posts/noteworthy`;
        // console.log('API URL:', url);
        
        return popinApi.get(url)
          .then(response => {
            // console.log('EventApi.fetchNoteworthy response summary:');
            // console.log('- Total noteworthy posts:', response.posts?.length || 0);
            if (response.posts && response.posts.length > 0) {
              // console.log('- First noteworthy post sample:', JSON.stringify(response.posts[0], null, 2));
              console.log('- Noteworthy post fields available:', Object.keys(response.posts[0]));
            }
            return response;
          });
      }
    }).catch(error => {
      console.error('EventApi.fetchNoteworthy error:', error);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      // This will be handled in the saga
      return { noteworthyError: error };
    });
  },

  markNoteworthy: (eventId, postIds) => {
    return Promise.resolve().then(() => {
      if (!eventId) {
        Logger.info('reject markNoteworthy');
        throw Error('You must pass eventId to markNoteworthy()');
      } else if (!postIds) {
        Logger.info('reject markNoteworthy');
        throw Error('You must pass postIds to markNoteworthy()');
      } else {
        return popinApi.post(`/events/${eventId}/posts/noteworthy`, { post_ids: postIds });
      }
    });
  },

  getSegmentationRoster: (eventId) => {
    return Promise.resolve().then(() => {
      if (!eventId) {
        Logger.info('reject getSegmentationRoster()');
        throw Error('Must pass an eventId as a param');
      } else {
        return popinApi.get(`/events/${eventId}/report/roster`);
      }
    }).catch(error => {
      // This will be handled in the saga
      return { rosterError: error };
    });
  },

  modifyRoster: (eventId, rosterId) => {
    return Promise.resolve().then(() => {
      if (!eventId) {
        Logger.info('reject modifyRoster()');
        throw Error('Must pass an eventId as a param');
      } else {
        return popinApi.post(`/events/edit`, {
          event_id: eventId,
          roster_id: rosterId
        });
      }
    });
  },

  extendPopinTime: (event) => {
    return Promise.resolve().then(() => {
      if (!event) {
        Logger.info('reject extendPopinTime()');
        throw Error('Must pass an eventId as a param');
      } else {
        // 60 seconds a minute * 60 minutes an hour * 24 hours a day
        const oneDaySeconds = 60 * 60 * 24;
        const newCloseTime = Event.getEndTime(event).unix() + oneDaySeconds;
        const startTime = Event.getStartTime(event).unix();
        return popinApi.post(`/events/edit`, {
          event_id: Event.getId(event),
          schedule: {
            ideas: {
              start_time: startTime,
              end_time: newCloseTime
            },
            voting: {
              start_time: startTime,
              end_time: newCloseTime
            }
          }
        });
      }
    });
  },

  getCsatStatus: (eventId) => {
    return Promise.resolve().then(() => {
      if (!eventId) {
        Logger.info('reject getCsatStatus()');
        throw Error('Must pass an eventId as a param');
      } else {
        return popinApi.get(`/events/${eventId}/csat/status`);
      }
    });
  },

  updateCsatStatus: (eventId, score, cancelled, userComment, promptQuestion) => {
    return Promise.resolve().then(() => {
      if (!eventId) {
        Logger.info('reject getCsatStatus()');
        throw Error('Must pass an eventId as a param');
      } else if (userComment === null || userComment === undefined) {
        Logger.info('reject getCsatStatus()');
        throw Error('Must pass a userComment as a param, can be blank');
      } else if (!promptQuestion) {
        Logger.info('reject getCsatStatus()');
        throw Error('Must pass a promptQuestion as a param');
      } else {
        return popinApi.post(`/events/${eventId}/csat`, {
          cancelled: cancelled,
          rating: score,
          comment: userComment,
          question: promptQuestion
        });
      }
    });
  },

  setStash: (key, obj) => {
    console.log("PUT", key, obj);
    localStorage.setItem(key, obj);
  },

  getStash: (key) => {
    var obj = localStorage.getItem(key);
    console.log("GET", key, obj);
    return obj;
  }

};
